import React, { useEffect, useState } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  SimulatorOrderConfig,
  SimulatorProduct,
  SimulatorQuestion,
  SimulatorTopLevelOrderItem,
  SimulatorRisk,
  SimulatorCover,
  SimulatorCoverItem,
} from 'api/types/simulator'
import { DotsSixVertical, CaretDown, CaretRight } from 'phosphor-react'

// ─── Drag handle ─────────────────────────────────────────────────────────────

const DragHandle = ({
  listeners,
  attributes,
}: {
  listeners: any
  attributes: any
}) => (
  <span
    {...listeners}
    {...attributes}
    className="cursor-grab active:cursor-grabbing text-metal-400 hover:text-metal-600 mr-2 shrink-0"
  >
    <DotsSixVertical size={16} />
  </span>
)

// ─── Sortable question row ────────────────────────────────────────────────────

const SortableQuestion = ({
  question,
  id,
}: {
  question: SimulatorQuestion
  id: string
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center px-3 py-2 rounded border border-metal-100 bg-white text-body-4 text-metal-700 ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <DragHandle listeners={listeners} attributes={attributes} />
      <span className="font-medium mr-2 text-metal-400">
        {question.QuestionCode}
      </span>
      <span>{question.QuestionText}</span>
      <span className="ml-auto text-body-5 text-metal-300">
        {question.Attribute}
      </span>
    </div>
  )
}

// ─── Sortable question list ───────────────────────────────────────────────────

const SortableQuestionList = ({
  questions,
  listId,
  onChange,
}: {
  questions: SimulatorQuestion[]
  listId: string
  onChange: (updated: SimulatorQuestion[]) => void
}) => {
  const sensors = useSensors(useSensor(PointerSensor))
  const ids = questions.map(q => `${listId}__${q.InputId}`)

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = ids.indexOf(active.id as string)
    const newIndex = ids.indexOf(over.id as string)
    onChange(arrayMove(questions, oldIndex, newIndex))
  }

  if (questions.length === 0)
    return <p className="text-body-5 text-metal-300 italic">Sem questões</p>

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-1">
          {questions.map((q, i) => (
            <SortableQuestion key={ids[i]} question={q} id={ids[i]} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

// ─── Sortable item row (inside a cover) ─────────────────────────────────────

const SortableCoverItem = ({
  item,
  id,
  onQuestionsChange,
}: {
  item: SimulatorCoverItem
  id: string
  onQuestionsChange: (updated: SimulatorQuestion[]) => void
}) => {
  const [open, setOpen] = useState(true)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`border border-metal-100 rounded bg-white ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-center px-3 py-2">
        <DragHandle listeners={listeners} attributes={attributes} />
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 flex-1 text-left text-body-4 text-metal-700"
        >
          {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
          <span className="text-metal-400 font-normal mr-1">
            {item.ItemCode}
          </span>
          {item.Description}
        </button>
      </div>
      {open && item.Questions.length > 0 && (
        <div className="px-4 pb-3">
          <p className="mb-1 text-body-5 font-semibold uppercase tracking-wide text-metal-400">
            Questões
          </p>
          <SortableQuestionList
            questions={item.Questions}
            listId={`item__${id}`}
            onChange={onQuestionsChange}
          />
        </div>
      )}
    </div>
  )
}

// ─── Sortable cover ───────────────────────────────────────────────────────────

const SortableCover = ({
  cover,
  id,
  riskCode,
  onCoverChange,
}: {
  cover: SimulatorCover
  id: string
  riskCode: string
  onCoverChange: (updated: SimulatorCover) => void
}) => {
  const [open, setOpen] = useState(true)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const sensors = useSensors(useSensor(PointerSensor))
  const itemIds = cover.Items.map(
    item => `${riskCode}__${cover.CoverCode}__item__${item.ItemCode}`,
  )

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = itemIds.indexOf(active.id as string)
    const newIndex = itemIds.indexOf(over.id as string)
    onCoverChange({
      ...cover,
      Items: arrayMove(cover.Items, oldIndex, newIndex),
    })
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`border border-metal-200 rounded-lg bg-metal-25 ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-center px-3 py-2">
        <DragHandle listeners={listeners} attributes={attributes} />
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 flex-1 text-left font-semibold text-body-4 text-metal-800"
        >
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <span className="text-metal-400 font-normal mr-1">
            {cover.CoverCode}
          </span>
          {cover.Description}
        </button>
      </div>
      {open && (
        <div className="space-y-3 px-4 pb-3">
          {cover.Questions.length > 0 && (
            <div>
              <p className="mb-1 text-body-5 font-semibold uppercase tracking-wide text-metal-500">
                Questões
              </p>
              <SortableQuestionList
                questions={cover.Questions}
                listId={`${riskCode}__${cover.CoverCode}`}
                onChange={updated =>
                  onCoverChange({ ...cover, Questions: updated })
                }
              />
            </div>
          )}
          {cover.Items.length > 0 && (
            <div>
              <p className="mb-1 text-body-5 font-semibold uppercase tracking-wide text-metal-500">
                Items
              </p>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleItemDragEnd}
              >
                <SortableContext
                  items={itemIds}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-1">
                    {cover.Items.map((item, i) => (
                      <SortableCoverItem
                        key={itemIds[i]}
                        item={item}
                        id={itemIds[i]}
                        onQuestionsChange={updated =>
                          onCoverChange({
                            ...cover,
                            Items: cover.Items.map(
                              (currentItem, currentIndex) =>
                                currentIndex === i
                                  ? { ...currentItem, Questions: updated }
                                  : currentItem,
                            ),
                          })
                        }
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Sortable group block (questions or covers group inside a risk) ───────────

const SortableRiskGroup = ({
  id,
  children,
}: {
  id: string
  children: React.ReactNode
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded border border-metal-100 p-3 ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-1">
        <span
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing text-metal-300 hover:text-metal-500 mt-0.5 shrink-0"
        >
          <DotsSixVertical size={14} />
        </span>
        <div className="flex-1">{children}</div>
      </div>
    </div>
  )
}

// ─── Sortable risk ────────────────────────────────────────────────────────────

type RiskGroup = 'questions' | 'covers'

const getInitialRiskGroupOrder = (risk: SimulatorRisk): RiskGroup[] => [
  ...(risk.Questions.length > 0 ? (['questions'] as RiskGroup[]) : []),
  ...(risk.Covers.length > 0 ? (['covers'] as RiskGroup[]) : []),
]

const SortableRisk = ({
  risk,
  id,
  groupOrder,
  onGroupOrderChange,
  onRiskChange,
}: {
  risk: SimulatorRisk
  id: string
  groupOrder: RiskGroup[]
  onGroupOrderChange: (updated: RiskGroup[]) => void
  onRiskChange: (updated: SimulatorRisk) => void
}) => {
  const [open, setOpen] = useState(true)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const sensors = useSensors(useSensor(PointerSensor))

  const groupIds = groupOrder.map(g => `risk__${risk.RiskCode}__group__${g}`)

  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = groupIds.indexOf(active.id as string)
    const newIndex = groupIds.indexOf(over.id as string)
    onGroupOrderChange(arrayMove(groupOrder, oldIndex, newIndex))
  }

  const coverIds = risk.Covers.map(
    c => `risk__${risk.RiskCode}__cover__${c.CoverCode}`,
  )

  const handleCoverDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = coverIds.indexOf(active.id as string)
    const newIndex = coverIds.indexOf(over.id as string)
    onRiskChange({
      ...risk,
      Covers: arrayMove(risk.Covers, oldIndex, newIndex),
    })
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`border border-metal-200 rounded-lg bg-white ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-center px-3 py-3">
        <DragHandle listeners={listeners} attributes={attributes} />
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 flex-1 text-left font-bold text-body-3 text-metal-900"
        >
          {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
          <span className="mr-1 font-normal text-metal-400">
            {risk.RiskCode}
          </span>
          {risk.Description}
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleGroupDragEnd}
          >
            <SortableContext
              items={groupIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-4">
                {groupOrder.map((group, i) => (
                  <SortableRiskGroup key={groupIds[i]} id={groupIds[i]}>
                    {group === 'questions' ? (
                      <>
                        <p className="mb-1 text-body-5 font-semibold uppercase tracking-wide text-metal-500">
                          Questões
                        </p>
                        <SortableQuestionList
                          questions={risk.Questions}
                          listId={`risk__${risk.RiskCode}__questions`}
                          onChange={updated =>
                            onRiskChange({ ...risk, Questions: updated })
                          }
                        />
                      </>
                    ) : (
                      <>
                        <p className="mb-2 text-body-5 font-semibold uppercase tracking-wide text-metal-500">
                          Coberturas
                        </p>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleCoverDragEnd}
                        >
                          <SortableContext
                            items={coverIds}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="flex flex-col gap-2">
                              {risk.Covers.map((cover, ci) => (
                                <SortableCover
                                  key={coverIds[ci]}
                                  cover={cover}
                                  id={coverIds[ci]}
                                  riskCode={risk.RiskCode}
                                  onCoverChange={updated =>
                                    onRiskChange({
                                      ...risk,
                                      Covers: risk.Covers.map((c, idx) =>
                                        idx === ci ? updated : c,
                                      ),
                                    })
                                  }
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </>
                    )}
                  </SortableRiskGroup>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  )
}

// ─── Top-level questions block (draggable alongside risks) ───────────────────

const TopLevelQuestionsBlock = ({
  id,
  questions,
  onChange,
}: {
  id: string
  questions: SimulatorQuestion[]
  onChange: (updated: SimulatorQuestion[]) => void
}) => {
  const [open, setOpen] = useState(true)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`border border-metal-200 rounded-lg bg-white ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-center px-3 py-3">
        <DragHandle listeners={listeners} attributes={attributes} />
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 flex-1 text-left font-bold text-body-3 text-metal-900"
        >
          {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
          Questões Gerais
        </button>
      </div>
      {open && (
        <div className="px-4 pb-4">
          <SortableQuestionList
            questions={questions}
            listId="top__questions"
            onChange={onChange}
          />
        </div>
      )}
    </div>
  )
}

// ─── Top-level context: [Questions block, Risk, Risk, ...] ───────────────────

type TopItem = { type: 'questions' } | { type: 'risk'; riskCode: string }

const getTopOrderSnapshot = (
  topOrder: TopItem[],
): SimulatorTopLevelOrderItem[] =>
  topOrder.map(item =>
    item.type === 'questions'
      ? 'questions'
      : (`risk:${item.riskCode}` as SimulatorTopLevelOrderItem),
  )

const getInitialTopOrder = (simulator: SimulatorProduct): TopItem[] => [
  { type: 'questions' },
  ...simulator.Risks.map(r => ({
    type: 'risk' as const,
    riskCode: r.RiskCode,
  })),
]

const getInitialRiskGroupOrders = (
  simulator: SimulatorProduct,
): Record<string, RiskGroup[]> =>
  Object.fromEntries(
    simulator.Risks.map(risk => [
      risk.RiskCode,
      getInitialRiskGroupOrder(risk),
    ]),
  )

const getOrderConfig = (
  simulator: SimulatorProduct,
  topOrder: TopItem[],
  riskGroupOrders: Record<string, RiskGroup[]>,
): SimulatorOrderConfig => ({
  topLevel: getTopOrderSnapshot(topOrder),
  riskGroups: Object.fromEntries(
    simulator.Risks.map(risk => [
      risk.RiskCode,
      riskGroupOrders[risk.RiskCode] ?? getInitialRiskGroupOrder(risk),
    ]),
  ),
})

export const SortableSimulator = ({
  simulator,
  onChange,
  onOrderConfigChange,
}: {
  simulator: SimulatorProduct
  onChange: (updated: SimulatorProduct) => void
  onOrderConfigChange: (updated: SimulatorOrderConfig) => void
}) => {
  const sensors = useSensors(useSensor(PointerSensor))

  const [topOrder, setTopOrder] = useState<TopItem[]>(() =>
    getInitialTopOrder(simulator),
  )
  const [riskGroupOrders, setRiskGroupOrders] = useState<
    Record<string, RiskGroup[]>
  >(() => getInitialRiskGroupOrders(simulator))

  useEffect(() => {
    setTopOrder(getInitialTopOrder(simulator))
    setRiskGroupOrders(getInitialRiskGroupOrders(simulator))
  }, [simulator.ProductCode])

  const topIds = topOrder.map(item =>
    item.type === 'questions' ? '__questions__' : `risk__${item.riskCode}`,
  )

  const handleTopDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = topIds.indexOf(active.id as string)
    const newIndex = topIds.indexOf(over.id as string)
    const newOrder = arrayMove(topOrder, oldIndex, newIndex)
    setTopOrder(newOrder)
    onOrderConfigChange(getOrderConfig(simulator, newOrder, riskGroupOrders))
    const newRisks = newOrder
      .filter(
        (item): item is { type: 'risk'; riskCode: string } =>
          item.type === 'risk',
      )
      .map(item => simulator.Risks.find(r => r.RiskCode === item.riskCode)!)
    onChange({ ...simulator, Risks: newRisks })
  }

  const updateRisk = (riskCode: string, updated: SimulatorRisk) => {
    onChange({
      ...simulator,
      Risks: simulator.Risks.map(r => (r.RiskCode === riskCode ? updated : r)),
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleTopDragEnd}
    >
      <SortableContext items={topIds} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-3">
          {topOrder.map(item => {
            if (item.type === 'questions') {
              return (
                <TopLevelQuestionsBlock
                  key="__questions__"
                  id="__questions__"
                  questions={simulator.Questions}
                  onChange={updated =>
                    onChange({ ...simulator, Questions: updated })
                  }
                />
              )
            }
            const risk = simulator.Risks.find(r => r.RiskCode === item.riskCode)
            if (!risk) return null
            return (
              <SortableRisk
                key={`risk__${risk.RiskCode}`}
                id={`risk__${risk.RiskCode}`}
                risk={risk}
                groupOrder={
                  riskGroupOrders[risk.RiskCode] ??
                  getInitialRiskGroupOrder(risk)
                }
                onGroupOrderChange={updated => {
                  const nextRiskGroupOrders = {
                    ...riskGroupOrders,
                    [risk.RiskCode]: updated,
                  }
                  setRiskGroupOrders(nextRiskGroupOrders)
                  onOrderConfigChange(
                    getOrderConfig(simulator, topOrder, nextRiskGroupOrders),
                  )
                }}
                onRiskChange={updated => updateRisk(risk.RiskCode, updated)}
              />
            )
          })}
        </div>
      </SortableContext>
    </DndContext>
  )
}
